import { BRAND } from '@/config/brand';
import type { AnalyticsAdapter } from './AnalyticsAdapter';
import type { AnalyticsEventName, AnalyticsPayload } from './events';

const SCRIPT_ID = BRAND.analyticsScriptId;

interface PlausibleWindow extends Window {
  plausible?: ((event: string, options?: { props: Record<string, unknown> }) => void) & {
    q?: unknown[];
  };
}

/**
 * Cookieless, IP-anonymising, no cross-site identifiers — the only kind of
 * analytics that fits a product whose pitch is "your data stays on this
 * device". The script is injected only after consent and removed on withdrawal.
 *
 * Deployment note: the shipped CSP is `script-src 'self'`. Using this adapter
 * requires adding the provider origin to `script-src` and `connect-src` in the
 * host configuration, or the injection is blocked with no runtime error.
 */
export class PlausibleAnalyticsAdapter implements AnalyticsAdapter {
  readonly id = 'plausible';

  constructor(private readonly domain: string, private readonly host = 'https://plausible.io') {
    this.mount();
  }

  private mount(): void {
    if (typeof document === 'undefined' || document.getElementById(SCRIPT_ID)) return;

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.defer = true;
    script.dataset.domain = this.domain;
    script.src = `${this.host}/js/script.js`;
    document.head.appendChild(script);
  }

  track<T extends AnalyticsEventName>(event: T, payload: AnalyticsPayload<T>): void {
    const target = window as PlausibleWindow;
    target.plausible?.(event, { props: payload as Record<string, unknown> });
  }

  shutdown(): void {
    document.getElementById(SCRIPT_ID)?.remove();
    delete (window as PlausibleWindow).plausible;
  }
}
